import { useState, useCallback, useEffect, useRef } from 'react';
import { AxiosError } from 'axios';

interface UseApiState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

interface UseApiResult<T> extends UseApiState<T> {
  execute: (...args: any[]) => Promise<T | null>;
  reset: () => void;
}

export type { UseApiState, UseApiResult };

export const useApi = <T,>(
  apiFunction: (...args: any[]) => Promise<T>,
  options?: {
    onSuccess?: (data: T) => void;
    onError?: (error: string) => void;
    autoExecute?: boolean;
  }
): UseApiResult<T> => {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    isLoading: options?.autoExecute !== false && options?.autoExecute !== undefined
      ? true  // показываем loading сразу если autoExecute=true
      : false,
    error: null,
  });

  // Используем ref чтобы не пересоздавать execute при изменении apiFunction
  const apiFunctionRef = useRef(apiFunction);
  apiFunctionRef.current = apiFunction;

  const execute = useCallback(async (...args: any[]) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await apiFunctionRef.current(...args);
      setState({ data: result, isLoading: false, error: null });

      if (options?.onSuccess) {
        options.onSuccess(result);
      }

      return result;
    } catch (err: any) {
      const errorMessage =
        err instanceof AxiosError
          ? err.response?.data?.message || err.message
          : (err as Error).message || 'Ошибка запроса';

      setState({ data: null, isLoading: false, error: errorMessage });

      if (options?.onError) {
        options.onError(errorMessage);
      }

      return null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Автовызов при монтировании если autoExecute=true
  const executedRef = useRef(false);
  useEffect(() => {
    if (options?.autoExecute && !executedRef.current) {
      executedRef.current = true;
      execute();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback(() => {
    setState({ data: null, isLoading: false, error: null });
    executedRef.current = false;
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
};
